import { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg } from "../../lib/api";
import { Btn, Loader, ErrorNote, Empty, shortDate } from "./kit";
import { Modal } from "./Form";

const useQuiz = (open, path) => {
  const [s, setS] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    if (!open || !path) return undefined;
    let live = true;
    setS({ loading: true, data: null, error: null });
    web.get(path).then(({ data }) => live && setS({ loading: false, data, error: null }))
      .catch((e) => live && setS({ loading: false, data: null, error: errMsg(e) }));
    return () => { live = false; };
  }, [open, path]);
  return s;
};

const Choice = ({ i, k, text, picked, result, onPick }) => {
  const right = result && result.answer === k;
  const wrong = result && picked && !result.correct;
  const tone = right ? "border-[#39FF14] bg-[#39FF14]/10 text-white" : wrong ? "border-[#FF3B30] bg-[#FF3B30]/10 text-red-100" : picked ? "border-[#39FF14] bg-[#162032] text-white" : "border-[#1E293B] bg-[#0F172A] text-slate-200 hover:border-[#39FF14]/50";
  return (
    <button type="button" disabled={!!result} data-testid={`take-q-${i}-choice-${k}`} onClick={onPick}
      className={`flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors ${tone}`}>
      <span className={`h-4 w-4 shrink-0 rounded-full border-2 ${picked ? "border-[#39FF14] bg-[#39FF14]" : "border-slate-500"}`} />
      <span className="flex-1">{text}</span>
      {right && <CheckCircle2 className="h-4 w-4 text-[#39FF14]" />}
      {wrong && <XCircle className="h-4 w-4 text-[#FF3B30]" />}
    </button>
  );
};

/** Player: take the quiz, see right/wrong answers after submitting, retake any time. */
export const QuizTake = ({ open, onClose, tid, play, onDone }) => {
  const path = play && `/teams/${tid}/plays/${play.id}/quiz`;
  const s = useQuiz(open, path);
  const [answers, setAnswers] = useState([]);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = Array.isArray(s.data?.quiz?.questions) ? s.data.quiz.questions : [];
  useEffect(() => { setAnswers(qs.map(() => null)); setRes(null); }, [s.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (answers.some((a) => a === null)) return toast.error("Answer every question");
    setBusy(true);
    try {
      const { data } = await web.post(`${path}/attempts`, { answers });
      setRes(data);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const retry = () => { setAnswers(qs.map(() => null)); setRes(null); };
  const best = res?.mine?.best ?? s.data?.mine?.best;

  return (
    <Modal open={open} onClose={onClose} testid="quiz-take" title="Play quiz" description={`${play?.title ?? ""} · Pass with ${s.data?.quiz?.pass_pct ?? 80}%${best != null ? ` · Your best: ${best}%` : ""}`}>
      {s.loading ? <Loader /> : s.error ? <ErrorNote error={s.error} /> : !qs.length ? <Empty title="No quiz yet" /> : (
        <div className="mt-2 space-y-5">
          {res && (
            <div data-testid="quiz-score" className={`rounded-xl px-4 py-3 ${res.passed ? "bg-[#39FF14]/15 text-[#39FF14]" : "bg-[#FF3B30]/15 text-red-200"}`}>
              <p className="font-display text-3xl font-black uppercase">{res.score_pct}% · {res.passed ? "Passed" : "Not yet"}</p>
              <p className="text-sm">{res.correct} of {res.total} right. {res.passed ? "You know this play." : `You need ${res.pass_pct}%. Study the play and try again.`}</p>
            </div>
          )}
          <ol className="space-y-4">
            {qs.map((q, i) => (
              <li key={i} data-testid={`take-q-${i}`} className="space-y-2">
                <p className="text-sm font-semibold text-white"><span className="mr-2 font-mono-gd text-xs text-[#39FF14]">Q{i + 1}</span>{q.text}</p>
                {q.choices.map((c, k) => <Choice key={k} i={i} k={k} text={c} picked={answers[i] === k} result={res?.results?.[i]} onPick={() => setAnswers(answers.map((a, j) => (j === i ? k : a)))} />)}
              </li>
            ))}
          </ol>
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={onClose} data-testid="quiz-take-close">Close</Btn>
            {res ? <Btn onClick={retry} data-testid="quiz-retry">Try again</Btn> : <Btn onClick={submit} disabled={busy} data-testid="quiz-submit">{busy ? "Checking…" : "Submit answers"}</Btn>}
          </div>
        </div>
      )}
    </Modal>
  );
};

/** Coach: who passed, who hasn't taken it, best score per player. */
export const QuizResults = ({ open, onClose, tid, play }) => {
  const s = useQuiz(open, play && `/teams/${tid}/plays/${play.id}/quiz`);
  const rows = Array.isArray(s.data?.results) ? [...s.data.results].sort((a, b) => (b.best ?? -1) - (a.best ?? -1)) : [];
  const passed = rows.filter((r) => r.passed).length;
  return (
    <Modal open={open} onClose={onClose} testid="quiz-results" title="Quiz results" description={`${play?.title ?? ""} · ${passed} of ${rows.length} passed`}>
      {s.loading ? <Loader /> : s.error ? <ErrorNote error={s.error} /> : !s.data?.quiz ? <Empty title="No quiz yet" /> : !rows.length ? <Empty title="No players on the roster" /> : (
        <ul className="mt-2 divide-y divide-[#1E293B] rounded-xl border border-[#1E293B]">
          {rows.map((r) => (
            <li key={r.player_id} data-testid={`quiz-result-${r.player_id}`} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span className="min-w-0 truncate text-white"><span className="mr-2 font-mono-gd text-xs text-slate-400">#{r.jersey ?? "–"}</span>{r.name}</span>
              <span className="shrink-0 text-right font-mono-gd text-xs uppercase">
                {r.attempts ? <><span className={r.passed ? "text-[#39FF14]" : "text-amber-300"}>{r.passed ? "Passed" : "Not passed"} · {r.best}%</span><span className="block text-slate-400">{r.attempts} tries · {shortDate(r.last_at)}</span></> : <span className="text-slate-400">Not taken</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
};
